// Safe PEMDAS expression evaluator using shunting-yard + RPN.
// Supports: + - * / ( ) and decimal numbers.
// Returns null on any error (divide-by-zero, malformed expression).

type Token =
  | { type: 'num'; value: number }
  | { type: 'op'; value: '+' | '-' | '*' | '/' }
  | { type: 'lp' }
  | { type: 'rp' }

const PREC: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 }

function tokenize(expr: string): Token[] | null {
  const tokens: Token[] = []
  let i = 0
  while (i < expr.length) {
    const ch = expr[i]
    if (ch === ' ') { i++; continue }
    if (ch === '(') { tokens.push({ type: 'lp' }); i++; continue }
    if (ch === ')') { tokens.push({ type: 'rp' }); i++; continue }
    if (ch === '+' || ch === '*' || ch === '/') {
      tokens.push({ type: 'op', value: ch })
      i++
      continue
    }
    if (ch === '-') {
      // Decide unary vs binary
      const prev = tokens[tokens.length - 1]
      const isUnary = !prev || prev.type === 'op' || prev.type === 'lp'
      if (isUnary) {
        // Read number after unary minus
        let j = i + 1
        let numStr = '-'
        let dot = 0
        while (j < expr.length && (/[0-9.]/.test(expr[j]))) {
          if (expr[j] === '.') { dot++; if (dot > 1) return null }
          numStr += expr[j]
          j++
        }
        if (numStr === '-') return null
        const n = parseFloat(numStr)
        if (Number.isNaN(n)) return null
        tokens.push({ type: 'num', value: n })
        i = j
        continue
      } else {
        tokens.push({ type: 'op', value: '-' })
        i++
        continue
      }
    }
    if (/[0-9.]/.test(ch)) {
      let numStr = ''
      let dot = 0
      while (i < expr.length && /[0-9.]/.test(expr[i])) {
        if (expr[i] === '.') { dot++; if (dot > 1) return null }
        numStr += expr[i]
        i++
      }
      const n = parseFloat(numStr)
      if (Number.isNaN(n)) return null
      tokens.push({ type: 'num', value: n })
      continue
    }
    return null
  }
  return tokens
}

function toRPN(tokens: Token[]): Token[] | null {
  const out: Token[] = []
  const stack: Token[] = []
  for (const t of tokens) {
    if (t.type === 'num') out.push(t)
    else if (t.type === 'op') {
      while (stack.length) {
        const top = stack[stack.length - 1]
        if (top.type === 'op' && PREC[top.value] >= PREC[t.value]) {
          out.push(stack.pop()!)
        } else break
      }
      stack.push(t)
    } else if (t.type === 'lp') stack.push(t)
    else if (t.type === 'rp') {
      while (stack.length && stack[stack.length - 1].type !== 'lp') {
        out.push(stack.pop()!)
      }
      if (!stack.length) return null
      stack.pop() // discard '('
    }
  }
  while (stack.length) {
    const top = stack.pop()!
    if (top.type === 'lp' || top.type === 'rp') return null
    out.push(top)
  }
  return out
}

function evalRPN(rpn: Token[]): number | null {
  const st: number[] = []
  for (const t of rpn) {
    if (t.type === 'num') st.push(t.value)
    else if (t.type === 'op') {
      if (st.length < 2) return null
      const b = st.pop()!
      const a = st.pop()!
      let r: number
      switch (t.value) {
        case '+': r = a + b; break
        case '-': r = a - b; break
        case '*': r = a * b; break
        case '/':
          if (b === 0) return null
          r = a / b
          break
      }
      st.push(r)
    }
  }
  if (st.length !== 1) return null
  return st[0]
}

export function evaluateExpression(expr: string): number | null {
  if (!expr || !expr.trim()) return null
  const tokens = tokenize(expr)
  if (!tokens) return null
  const rpn = toRPN(tokens)
  if (!rpn) return null
  const result = evalRPN(rpn)
  if (result === null || !Number.isFinite(result)) return null
  return result
}

// Format result to fit 8-digit display, returns "ERROR" if it overflows.
export function formatResult(n: number | null): string {
  if (n === null) return 'ERROR'
  if (!Number.isFinite(n)) return 'ERROR'
  if (Math.abs(n) > 99999999) return 'ERROR'
  // Round to fit 8 digits including decimal point
  let str: string
  if (Number.isInteger(n)) {
    str = String(n)
  } else {
    // Allocate digits to integer part first
    const intPart = Math.trunc(n)
    const intDigits = String(Math.abs(intPart)).length
    const sign = n < 0 ? 1 : 0
    const decAvail = Math.max(0, 8 - intDigits - sign - 1) // -1 for decimal point
    str = n.toFixed(decAvail)
    // Trim trailing zeros
    if (str.includes('.')) str = str.replace(/0+$/, '').replace(/\.$/, '')
  }
  // Strip the decimal point and minus when counting digits
  const digitCount = str.replace(/[-.]/g, '').length
  if (digitCount > 8) return 'ERROR'
  return str
}
